import { createListenerMiddleware } from '@reduxjs/toolkit';

import { urlUtils, queryKeys } from '../../utils/url';

import { initSucceeded } from '../actions/cruiseListActions';
import {
  cruiseCategorySelected,
  departureMonthSelected,
  destinationSelected,
  durationSelected,
  filtersCleared,
  numberOfDestinationsSelected,
  shipRatingSelected,
  shippingCompanySelected,
  singleFilterCleared,
} from '../reducers/cruiseList/filter';
import { sortOrderSelected } from '../reducers/cruiseList/sort';

// **The port is behaviour-preserving because of two measured properties of
// `createListenerMiddleware`.** A listener effect runs *synchronously inside* `dispatch`, before
// it returns, so every URL write below keeps the timing it had; and `listenerApi.getState()` is
// the **post-reducer** state, which is the like-for-like read for the old middleware, since that
// called `next(action)` as its first statement and read state afterwards.
// `listenerApi.getOriginalState()` is the pre-action state and is what step 9's `hotelListQuery`
// needs instead — see the middleware appendix in docs/rtk-migration-plan-2026-09.md.
//
// One difference, in a direction nothing can depend on: the old middleware never returned
// `next(action)`, so `store.dispatch` returned `undefined` for every cruise-list action. It now
// returns the action, which is standard redux behaviour.
//
// The `actionsWithQueryUpdate` array and the switch are gone: one listener per action type says
// the same thing, and a matcher on the action creator cannot drift from the reducer that owns it
// the way a shared string constant could.
const listenerMiddleware = createListenerMiddleware();

// The five groups whose query value is just the selected list, joined. `updateQueryWithValues`
// deletes a key whose value is falsy, and `[].join()` is the empty string, so deselecting the last
// option removes the parameter rather than writing an empty one.
const SELECTED_LIST_GROUPS = [
  [departureMonthSelected, 'departureMonth', queryKeys.CRUISE_FILTERS.departureMonth],
  [shipRatingSelected, 'shipRating', queryKeys.CRUISE_FILTERS.shipRating],
  [cruiseCategorySelected, 'cruiseCategory', queryKeys.CRUISE_FILTERS.cruiseCategory],
  [destinationSelected, 'destination', queryKeys.CRUISE_FILTERS.destination],
  [shippingCompanySelected, 'shippingCompany', queryKeys.CRUISE_FILTERS.shippingCompany],
];

SELECTED_LIST_GROUPS.forEach(([actionCreator, groupKey, queryKey]) => {
  listenerMiddleware.startListening({
    actionCreator,
    effect: (_action, listenerApi) => {
      const { selected } = listenerApi.getState().filter.filterGroups[groupKey];
      urlUtils.updateQueryWithValues({ [queryKey]: selected.join() });
    },
  });
});

// `initSucceeded` mirrors the site's pre-selected filter into the URL, and only then. The reducer
// has already applied it to state by the time this runs.
listenerMiddleware.startListening({
  actionCreator: initSucceeded,
  effect: (action) => {
    if (!action.payload.preSelectedFilter) {
      return;
    }
    const filterState = urlUtils.getFilterStateFromQueryByFiltersType(
      queryKeys.CRUISE_FILTERS,
      action.payload.preSelectedFilter,
    );
    urlUtils.updateQueryWithValues(filterState);
  },
});

// Written for every dispatch, including the one the reducer's equality guard discards.
listenerMiddleware.startListening({
  actionCreator: sortOrderSelected,
  effect: (action) => {
    urlUtils.updateUrlWithSortKey(action.payload);
  },
});

// Both range filters write a `min,max` pair, and omit the parameter entirely while the selection
// still spans the full available range.
listenerMiddleware.startListening({
  actionCreator: durationSelected,
  effect: (_action, listenerApi) => {
    const { minValue, maxValue, selectedMinValue, selectedMaxValue } =
      listenerApi.getState().filter.filterGroups.duration;
    const selectedValuesAreNotDefault = selectedMinValue !== minValue || selectedMaxValue !== maxValue;

    urlUtils.updateQueryWithValues({
      [queryKeys.CRUISE_FILTERS.duration]: selectedValuesAreNotDefault
        ? `${selectedMinValue},${selectedMaxValue}`
        : undefined,
    });
  },
});

listenerMiddleware.startListening({
  actionCreator: numberOfDestinationsSelected,
  effect: (_action, listenerApi) => {
    const {
      minNumberOfDestinations,
      maxNumberOfDestinations,
      selectedMinNumberOfDestinations,
      selectedMaxNumberOfDestinations,
    } = listenerApi.getState().filter.filterGroups.destination;
    const selectedValuesAreNotDefault =
      selectedMinNumberOfDestinations !== minNumberOfDestinations ||
      selectedMaxNumberOfDestinations !== maxNumberOfDestinations;

    urlUtils.updateQueryWithValues({
      [queryKeys.CRUISE_FILTERS.numberOfDestinations]: selectedValuesAreNotDefault
        ? `${selectedMinNumberOfDestinations},${selectedMaxNumberOfDestinations}`
        : undefined,
    });
  },
});

listenerMiddleware.startListening({
  actionCreator: filtersCleared,
  effect: () => {
    const filterQueriesToRemove = {};

    Object.keys(queryKeys.CRUISE_FILTERS).forEach((queryKey) => {
      filterQueriesToRemove[queryKeys.CRUISE_FILTERS[queryKey]] = undefined;
    });

    urlUtils.updateQueryWithValues(filterQueriesToRemove);
  },
});

// An unrecognised group key writes `{ undefined: undefined }`, which deletes nothing. Preserved.
listenerMiddleware.startListening({
  actionCreator: singleFilterCleared,
  effect: (action) => {
    urlUtils.updateQueryWithValues({ [queryKeys.CRUISE_FILTERS[action.payload]]: undefined });
  },
});

export default listenerMiddleware.middleware;
